import { Ensure, Failure, ResultMap, Success } from "@pdfking/errors"
import { BigintQ, BytesQ, NullQ, StringQ } from "@pdfking/structs"
import { Constant, Identity, StringToUInt8 } from "@pdfking/tfunc"

import type { AtomForm, ChoiceForm, Choices } from "#public"

import { CheckLength, CheckRange } from "../../internal/CheckRange"
import { EnsureString } from "../../internal/EnsureString"
import { CreateFormChoice, CreateFormField } from "../api/CreateForm"

// Every atom field accepts an `initial` getter and a `format` for its value.
// `format` is `NoInfer` so a wide-typed formatter (e.g. one taking a base
// `{ id, name }`) doesn't drag `T` down to that base — `T` comes from `choices`.
type Config<T> = {
  initial?: () => unknown
  format?: (value: NoInfer<T>) => string
  optional?: boolean
  suggestions?: Record<string, string | undefined>
}

type OptionalValue<T, Optional extends boolean> = Optional extends true ? T | undefined : T

export const AnyField = <const Optional extends boolean = false>(
  config: Config<unknown> & { optional?: Optional } = {}
): AtomForm<OptionalValue<unknown, Optional>> =>
  CreateFormField<unknown, Optional>({ validate: Success, ...config })

export const StringField = <const Optional extends boolean = false>({
  initial,
  ...rest
}: Config<string> & { optional?: Optional } = {}): AtomForm<OptionalValue<string, Optional>> =>
  CreateFormField<string, Optional>({
    validate: EnsureString,
    format: Identity,
    initial: initial ?? Constant(""),
    ...rest,
  })

export const IntegerField = <const Optional extends boolean = false>(
  config: Config<bigint> & {
    min?: bigint
    max?: bigint
    optional?: Optional
  } = {}
): AtomForm<OptionalValue<bigint, Optional>> =>
  CreateFormField<bigint, Optional>({
    validate: v =>
      ResultMap(Ensure(NullQ(v) || BigintQ(v) ? v : BigInt(v as string | number), BigintQ), n =>
        CheckRange(n, config, v)
      ),
    ...config,
  })

// A boolean is a two-state choice (keyed by the booleans' string forms, which
// also match raw `true`/`false` inputs once coerced to a key). When `required`
// it must be `true` — `false` fails REQUIRED_ERROR (e.g. a must-tick checkbox).
export const BooleanField = ({
  required = false,
  ...config
}: Config<boolean> & { required?: boolean } = {}): ChoiceForm<boolean> =>
  CreateFormChoice({
    choices: { true: true, false: false },
    validate: v =>
      required && v === false ? Failure("REQUIRED_ERROR", { input: v }) : Success(v as boolean),
    ...config,
  })

export const BytesField = <const Optional extends boolean = false>(
  config: Config<Uint8Array> & { optional?: Optional } = {}
): AtomForm<OptionalValue<Uint8Array, Optional>> =>
  CreateFormField<Uint8Array, Optional>({
    validate: v => Ensure(StringQ(v) ? StringToUInt8(v) : v, BytesQ),
    ...config,
  })

// A field over named `choices` (key → value). `CreateFormChoice` resolves the
// input key to its value (`CHOICE_ERROR` otherwise); `format` defaults to
// JSONEncode and can be overridden through `config`.
export const ChoiceField = <T>(choices: Choices<T>, config: Config<T> = {}): ChoiceForm<T> =>
  CreateFormChoice<T>({ choices, ...config })

// Like `ChoiceField` but multi-valued: the input is a list of keys and the value
// is the list of resolved values (`CHOICE_ERROR` if any key is unknown).
// `min`/`max` bound how many may be selected (MIN/MAX_LENGTH_ERROR on the count).
export const MultipleChoiceField = <T>(
  choices: Choices<T>,
  config: Config<T> & { min?: number; max?: number } = {}
): ChoiceForm<T[]> =>
  CreateFormChoice<T, true>({
    choices,
    multiple: true,
    validate: v => ResultMap(CheckLength((v as T[]).length, config, v), () => Success(v as T[])),
    initial: Constant([]),
    ...config,
  })
